/**
 * NfceCancelamentoService
 *
 * Cancela fiscalmente uma NFC-e autorizada no contexto PDV Desktop.
 *
 * Fluxo:
 *   1. Busca nfce_local pelo uuid_venda_terminal
 *   2. Valida status_fiscal === 'autorizada', chave, justificativa
 *   3. Chama POST /api/offline/nfce/cancelar no back_nuts
 *   4. Em sucesso: marca nfce_local como cancelada e (se houver caixa aberto)
 *      registra movimento de estorno usando registrarSangria.
 *   5. Em rejeição: mantém status 'autorizada' e grava ultimo_erro.
 *   6. Em falha de comunicação: mantém status 'autorizada' e devolve erro
 *      amigável.
 */
import { getElectronAPI, isDesktopMode } from "@/plugins/desktop";
import ApiOfflineService from "./ApiOfflineService";
import TerminalConfigService from "./TerminalConfigService";
import AppConfigRepository from "@/modules/PdvDesktop/repositories/AppConfigRepository";

const JUSTIFICATIVA_MIN = 15;
const JUSTIFICATIVA_MAX = 255;

function _validarJustificativa(justificativa) {
  const j = (justificativa ?? "").trim();
  if (!j || j.length < JUSTIFICATIVA_MIN || j.length > JUSTIFICATIVA_MAX) {
    throw new Error(`A justificativa deve ter entre ${JUSTIFICATIVA_MIN} e ${JUSTIFICATIVA_MAX} caracteres.`);
  }
  return j;
}

async function _registrarEstornoCaixa(api, nfce) {
  try {
    const caixaAberto = await api.caixa.buscarAberto();
    if (!caixaAberto || !caixaAberto.uuid_caixa) return;

    const numero = nfce.numero ?? nfce.numeroNota ?? "";
    const valor  = Math.abs(Number(nfce.valorTotal || 0));

    await api.caixa.registrarSangria({
      uuid:            crypto.randomUUID(),
      uuid_caixa:      caixaAberto.uuid_caixa,
      id_empresa:      caixaAberto.id_empresa ?? null,
      id_usuario:      caixaAberto.id_usuario ?? null,
      valor_movimento: valor,
      descricao:       `Cancelamento NFC-e número ${numero}`,
      payload_json:    JSON.stringify({
        tipo:                "cancelamento_nfce",
        uuid_venda_terminal: nfce.uuid_venda_terminal,
        chave_nfce:          nfce.chave ?? null,
        numero:              nfce.numero ?? nfce.numeroNota ?? null,
        serie:               nfce.serie ?? null,
        valor_original:      Number(nfce.valorTotal || 0),
      }),
    });
  } catch (e) {
    // não interrompe o cancelamento fiscal, mas loga
    console.warn("[NfceCancelamentoService] Falha ao registrar estorno no caixa:", e.message || e);
  }
}

const NfceCancelamentoService = {
  JUSTIFICATIVA_MIN,
  JUSTIFICATIVA_MAX,

  /**
   * Cancela fiscalmente uma NFC-e autorizada.
   *
   * @param {string} uuid_venda_terminal
   * @param {string} justificativa
   * @returns {Promise<{
   *   success: boolean,
   *   status?: string,
   *   protocolo_cancelamento?: string,
   *   xml_cancelamento?: string,
   *   motivo?: string,
   *   codigo?: string,
   * }>}
   */
  async cancelarNfce(uuid_venda_terminal, justificativa) {
    if (!isDesktopMode()) throw new Error("Modo desktop não disponível.");
    const api = getElectronAPI();
    if (!api) throw new Error("Electron API não disponível.");

    // Operação fiscal crítica: valida vínculo antes de cancelar
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });

    const justificativaLimpa = _validarJustificativa(justificativa);

    // 1. Carrega NFC-e local
    const nfce = await api.nfce.getByUuid(uuid_venda_terminal);
    if (!nfce) throw new Error(`NFC-e não encontrada: ${uuid_venda_terminal}`);

    // 2. Validações de pré-condição
    if (nfce.status_fiscal === "cancelada") {
      throw new Error("NFC-e já está cancelada.");
    }
    if (nfce.status_fiscal !== "autorizada") {
      throw new Error(
        "Somente NFC-e autorizada pode ser cancelada via SEFAZ. " +
        "Para notas não autorizadas, utilize 'Cancelar venda local'."
      );
    }
    if (!nfce.chave) {
      throw new Error("NFC-e sem chave de acesso. Não é possível cancelar via SEFAZ.");
    }
    if (!nfce.protocolo && !nfce.xml_autorizado) {
      throw new Error("NFC-e sem protocolo nem XML autorizado. Não é possível cancelar via SEFAZ.");
    }

    const [uuid_terminal, device_id] = await Promise.all([
      api.config.get("uuid_terminal"),
      AppConfigRepository.getDeviceId(),
    ]);

    if (!uuid_terminal || !device_id) {
      throw new Error("Terminal não configurado corretamente. Configure novamente o terminal.");
    }

    // 3. Envia solicitação ao back_nuts
    let resposta;
    try {
      resposta = await ApiOfflineService.cancelarNfce({
        uuid_terminal,
        device_id:           device_id || "",
        uuid_venda_terminal: nfce.uuid_venda_terminal,
        chave:               nfce.chave,
        justificativa:       justificativaLimpa,
      });
    } catch (errComm) {
      const msgTec = errComm.message || String(errComm);
      try {
        await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "autorizada", {
          ultimo_erro: `Cancelamento: ${msgTec}`,
        });
      } catch (_) { /* ignora */ }
      throw new Error(
        "Não foi possível concluir o cancelamento agora. Tente novamente em instantes."
      );
    }

    // O back_nuts retorna { success: boolean, protocolo_cancelamento?, xml_cancelamento?,
    //                        motivo?, tipo_erro?, mensagem_usuario?, mensagem_tecnica?, codigo? }
    const sucesso = resposta?.success === true;

    if (sucesso) {
      // 4. Persiste cancelamento e estorna caixa
      await api.nfce.cancelarFiscal(uuid_venda_terminal, {
        justificativa:          justificativaLimpa,
        protocolo_cancelamento: resposta.protocolo_cancelamento ?? null,
        xml_cancelamento:       resposta.xml_cancelamento       ?? null,
      });

      const nfceAtualizada = await api.nfce.getByUuid(uuid_venda_terminal);
      await _registrarEstornoCaixa(api, nfceAtualizada || nfce);

      return {
        success:                true,
        status:                 "cancelada",
        uuid_venda_terminal,
        nfce:                   nfceAtualizada || { ...nfce, status_fiscal: "cancelada" },
        protocolo_cancelamento: resposta.protocolo_cancelamento ?? null,
        xml_cancelamento:       resposta.xml_cancelamento       ?? null,
        motivo:                 resposta.motivo ?? resposta.msg ?? "Cancelamento homologado",
      };
    }

    // 5. Rejeição SEFAZ ou falha de comunicação — mantém autorizada
    const isFalhaComm = resposta?.tipo_erro === "comunicacao_sefaz";
    const motivo      = isFalhaComm
      ? (resposta?.mensagem_usuario || resposta?.msg || "Não foi possível comunicar com a SEFAZ.")
      : (resposta?.motivo || resposta?.msg || "Cancelamento recusado pela SEFAZ.");
    const codigo      = resposta?.codigo ?? null;

    const erroLog = isFalhaComm
      ? `Cancelamento (comunicação): ${resposta?.mensagem_tecnica || motivo}`
      : `Cancelamento: ${motivo}`;

    await api.nfce.atualizarStatusFiscal(uuid_venda_terminal, "autorizada", {
      ultimo_erro: erroLog,
    });

    return {
      success:   false,
      status:    resposta?.status || "erro_cancelamento",
      tipo_erro: resposta?.tipo_erro || null,
      motivo,
      codigo,
    };
  },
};

export default NfceCancelamentoService;
